/**
 * Incident Service
 *
 * Handles incident reporting and tracking for technicians in the field.
 * Supports safety incidents, equipment failures, delays, and other issues.
 *
 * DB table: incidents
 * Columns: id, project_id, task_id, reported_by, incident_type, severity,
 *          title, description, photos (TEXT[]), latitude, longitude,
 *          location_accuracy, location_description, work_stopped, status,
 *          reported_at, acknowledged_by, acknowledged_at, resolved_by,
 *          resolved_at, resolution_notes, created_at, updated_at
 *
 * @module services/incidentService
 */

import { supabase } from './supabase';
import {
  INCIDENT_TYPE,
  INCIDENT_SEVERITY,
  INCIDENT_STATUS,
  STORAGE_BUCKETS,
  PAGINATION,
} from '../utils/constants';

/**
 * Create a new incident report
 * @param {Object} data - Incident data
 * @param {string} data.project_id - Project ID
 * @param {string} data.task_id - Task ID (optional)
 * @param {string} data.incident_type - Type of incident
 * @param {string} data.severity - Incident severity
 * @param {string} data.title - Incident title
 * @param {string} data.description - Detailed description
 * @param {Array} data.photos - Array of photo URLs (optional)
 * @param {number} data.latitude - GPS latitude (optional)
 * @param {number} data.longitude - GPS longitude (optional)
 * @param {number} data.location_accuracy - GPS accuracy (optional)
 * @param {string} data.location_description - Location description (optional)
 * @param {boolean} data.work_stopped - Whether work was stopped (optional)
 * @returns {Promise<{data, error}>}
 */
export const createIncident = async (data) => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Validate required fields
    if (!data.project_id) throw new Error('Se requiere el proyecto');
    if (!data.incident_type) throw new Error('Se requiere el tipo de incidente');
    if (!data.title) throw new Error('Se requiere el titulo del incidente');
    if (!data.description) throw new Error('Se requiere la descripcion del incidente');

    const { data: incident, error } = await supabase
      .from('incidents')
      .insert({
        project_id: data.project_id,
        task_id: data.task_id || null,
        reported_by: user.id,
        incident_type: data.incident_type,
        severity: data.severity || INCIDENT_SEVERITY.MEDIUM,
        title: data.title,
        description: data.description,
        photos: data.photos || [],
        latitude: data.latitude || null,
        longitude: data.longitude || null,
        location_accuracy: data.location_accuracy || null,
        location_description: data.location_description || null,
        work_stopped: data.work_stopped || false,
        status: INCIDENT_STATUS.REPORTED,
      })
      .select(`
        *,
        project:projects(id, project_number, title),
        task:tasks(id, title, task_number),
        reported_by_profile:profiles!reported_by(id, full_name, phone)
      `)
      .single();

    if (error) throw error;

    return { data: incident, error: null };
  } catch (error) {
    console.error('[INCIDENT] Create incident error:', error);
    return { data: null, error };
  }
};

/**
 * Get incidents with optional filters
 * @param {Object} filters - Filter options
 * @param {string} filters.projectId - Filter by project
 * @param {string} filters.type - Filter by incident type
 * @param {string} filters.severity - Filter by severity
 * @param {string} filters.status - Filter by status
 * @param {string} filters.reportedBy - Filter by reporter
 * @param {string} filters.dateFrom - Start date
 * @param {string} filters.dateTo - End date
 * @param {number} filters.page - Page number
 * @param {number} filters.pageSize - Items per page
 * @returns {Promise<{data, count, error}>}
 */
export const getIncidents = async (filters = {}) => {
  try {
    let query = supabase
      .from('incidents')
      .select(`
        *,
        project:projects(id, project_number, title, client:clients!projects_client_id_fkey(company_name, primary_contact_name)),
        task:tasks(id, title, task_number),
        reported_by_profile:profiles!reported_by(id, full_name, phone),
        acknowledged_by_profile:profiles!acknowledged_by(id, full_name)
      `, { count: 'exact' });

    // Apply filters
    if (filters.projectId) {
      query = query.eq('project_id', filters.projectId);
    }

    if (filters.type) {
      query = query.eq('incident_type', filters.type);
    }

    if (filters.severity) {
      query = query.eq('severity', filters.severity);
    }

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    if (filters.reportedBy) {
      query = query.eq('reported_by', filters.reportedBy);
    }

    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }

    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }

    // Apply pagination
    const page = filters.page || 0;
    const pageSize = filters.pageSize || PAGINATION.DEFAULT_PAGE_SIZE;
    const from = page * pageSize;
    const to = from + pageSize - 1;

    query = query
      .range(from, to)
      .order('created_at', { ascending: false });

    const { data, count, error } = await query;

    if (error) throw error;

    return { data, count, error: null };
  } catch (error) {
    console.error('[INCIDENT] Get incidents error:', error);
    return { data: null, count: 0, error };
  }
};

/**
 * Get incidents by project
 * @param {string} projectId - Project ID
 * @param {Object} filters - Additional filters
 * @returns {Promise<{data, error}>}
 */
export const getIncidentsByProject = async (projectId, filters = {}) => {
  try {
    let query = supabase
      .from('incidents')
      .select(`
        *,
        task:tasks(id, title, task_number),
        reported_by_profile:profiles!reported_by(id, full_name, phone),
        acknowledged_by_profile:profiles!acknowledged_by(id, full_name)
      `)
      .eq('project_id', projectId);

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    if (filters.severity) {
      query = query.eq('severity', filters.severity);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[INCIDENT] Get by project error:', error);
    return { data: null, error };
  }
};

/**
 * Get single incident by ID
 * @param {string} incidentId - Incident ID
 * @returns {Promise<{data, error}>}
 */
export const getIncident = async (incidentId) => {
  try {
    const { data, error } = await supabase
      .from('incidents')
      .select(`
        *,
        project:projects(id, project_number, title, client:clients!projects_client_id_fkey(company_name, primary_contact_name, primary_contact_phone)),
        task:tasks(id, title, task_number),
        reported_by_profile:profiles!reported_by(id, full_name, phone),
        acknowledged_by_profile:profiles!acknowledged_by(id, full_name),
        resolved_by_profile:profiles!resolved_by(id, full_name)
      `)
      .eq('id', incidentId)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[INCIDENT] Get incident error:', error);
    return { data: null, error };
  }
};

/**
 * Acknowledge an incident (admin function)
 * @param {string} incidentId - Incident ID
 * @returns {Promise<{data, error}>}
 */
export const acknowledgeIncident = async (incidentId) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Verify incident exists and is in OPEN status
    const { data: existing, error: fetchError } = await supabase
      .from('incidents')
      .select('id, status')
      .eq('id', incidentId)
      .single();

    if (fetchError) throw new Error('Incidente no encontrado');

    if (existing.status !== INCIDENT_STATUS.REPORTED) {
      throw new Error('Solo se pueden reconocer incidentes reportados');
    }

    const { data, error } = await supabase
      .from('incidents')
      .update({
        status: INCIDENT_STATUS.ACKNOWLEDGED,
        acknowledged_by: user.id,
        acknowledged_at: new Date().toISOString(),
      })
      .eq('id', incidentId)
      .select(`
        *,
        project:projects(id, project_number, title),
        reported_by_profile:profiles!reported_by(id, full_name),
        acknowledged_by_profile:profiles!acknowledged_by(id, full_name)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[INCIDENT] Acknowledge incident error:', error);
    return { data: null, error };
  }
};

/**
 * Update incident status
 * @param {string} incidentId - Incident ID
 * @param {string} status - New status
 * @param {string} notes - Update notes (stored in resolution_notes if resolving)
 * @returns {Promise<{data, error}>}
 */
export const updateIncidentStatus = async (incidentId, status, notes = '') => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Validate status transition
    const validStatuses = Object.values(INCIDENT_STATUS);
    if (!validStatuses.includes(status)) {
      throw new Error('Estado no valido');
    }

    const updateData = { status };

    // Add status-specific fields
    if (status === INCIDENT_STATUS.ACKNOWLEDGED) {
      updateData.acknowledged_by = user.id;
      updateData.acknowledged_at = new Date().toISOString();
    }

    if (status === INCIDENT_STATUS.RESOLVED) {
      updateData.resolved_by = user.id;
      updateData.resolved_at = new Date().toISOString();
      updateData.resolution_notes = notes || null;
    }

    const { data, error } = await supabase
      .from('incidents')
      .update(updateData)
      .eq('id', incidentId)
      .select(`
        *,
        project:projects(id, project_number, title),
        reported_by_profile:profiles!reported_by(id, full_name)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[INCIDENT] Update status error:', error);
    return { data: null, error };
  }
};

/**
 * Resolve an incident
 * @param {string} incidentId - Incident ID
 * @param {string} resolutionNotes - Resolution details
 * @returns {Promise<{data, error}>}
 */
export const resolveIncident = async (incidentId, resolutionNotes) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    if (!resolutionNotes || resolutionNotes.trim() === '') {
      throw new Error('Se requieren notas de resolucion');
    }

    // Verify incident exists and can be resolved
    const { data: existing, error: fetchError } = await supabase
      .from('incidents')
      .select('id, status')
      .eq('id', incidentId)
      .single();

    if (fetchError) throw new Error('Incidente no encontrado');

    if (existing.status === INCIDENT_STATUS.RESOLVED || existing.status === INCIDENT_STATUS.CLOSED) {
      throw new Error('Este incidente ya fue resuelto o cerrado');
    }

    const { data, error } = await supabase
      .from('incidents')
      .update({
        status: INCIDENT_STATUS.RESOLVED,
        resolved_by: user.id,
        resolved_at: new Date().toISOString(),
        resolution_notes: resolutionNotes,
      })
      .eq('id', incidentId)
      .select(`
        *,
        project:projects(id, project_number, title),
        reported_by_profile:profiles!reported_by(id, full_name),
        resolved_by_profile:profiles!resolved_by(id, full_name)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[INCIDENT] Resolve incident error:', error);
    return { data: null, error };
  }
};

/**
 * Close an incident (final status)
 * @param {string} incidentId - Incident ID
 * @returns {Promise<{data, error}>}
 */
export const closeIncident = async (incidentId) => {
  try {
    const { data: existing, error: fetchError } = await supabase
      .from('incidents')
      .select('id, status')
      .eq('id', incidentId)
      .single();

    if (fetchError) throw new Error('Incidente no encontrado');

    if (existing.status !== INCIDENT_STATUS.RESOLVED) {
      throw new Error('Solo se pueden cerrar incidentes resueltos');
    }

    const { data, error } = await supabase
      .from('incidents')
      .update({
        status: INCIDENT_STATUS.CLOSED,
      })
      .eq('id', incidentId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[INCIDENT] Close incident error:', error);
    return { data: null, error };
  }
};

/**
 * Upload incident photo to storage
 * @param {File} file - Image file
 * @param {string} incidentId - Incident ID
 * @returns {Promise<{url, path, error}>}
 */
export const uploadIncidentPhoto = async (file, incidentId) => {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Tipo de archivo no permitido. Use JPG, PNG o WebP.');
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('El archivo excede el tamano maximo de 10MB.');
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `incidents/${incidentId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKETS.PROGRESS_PHOTOS)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    // Get signed URL
    const { data: urlData, error: urlError } = await supabase.storage
      .from(STORAGE_BUCKETS.PROGRESS_PHOTOS)
      .createSignedUrl(data.path, 3600); // 1-hour expiry (see CLAUDE.md storage policy)

    if (urlError) {
      const { data: { publicUrl } } = supabase.storage
        .from(STORAGE_BUCKETS.PROGRESS_PHOTOS)
        .getPublicUrl(data.path);
      return { url: publicUrl, path: data.path, error: null };
    }

    return { url: urlData.signedUrl, path: data.path, error: null };
  } catch (error) {
    console.error('[INCIDENT] Upload photo error:', error);
    return { url: null, path: null, error };
  }
};

/**
 * Get incident statistics
 * @param {Object} filters - Filter options
 * @param {string} filters.projectId - Filter by project
 * @param {string} filters.dateFrom - Start date
 * @param {string} filters.dateTo - End date
 * @returns {Promise<{data, error}>}
 */
export const getIncidentStats = async (filters = {}) => {
  try {
    let query = supabase
      .from('incidents')
      .select('id, incident_type, severity, status, created_at');

    if (filters.projectId) {
      query = query.eq('project_id', filters.projectId);
    }

    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }

    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }

    const { data, error } = await query;

    if (error) throw error;

    const stats = {
      total: data.length,
      byType: {},
      bySeverity: {},
      byStatus: {},
    };

    // Count by type
    Object.values(INCIDENT_TYPE).forEach(type => {
      stats.byType[type] = data.filter(i => i.incident_type === type).length;
    });

    // Count by severity
    Object.values(INCIDENT_SEVERITY).forEach(sev => {
      stats.bySeverity[sev] = data.filter(i => i.severity === sev).length;
    });

    // Count by status
    Object.values(INCIDENT_STATUS).forEach(status => {
      stats.byStatus[status] = data.filter(i => i.status === status).length;
    });

    return { data: stats, error: null };
  } catch (error) {
    console.error('[INCIDENT] Get stats error:', error);
    return { data: null, error };
  }
};

/**
 * Get open incidents (not resolved or closed)
 * @param {Object} filters - Filter options
 * @returns {Promise<{data, error}>}
 */
export const getOpenIncidents = async (filters = {}) => {
  try {
    let query = supabase
      .from('incidents')
      .select(`
        *,
        project:projects(id, project_number, title),
        reported_by_profile:profiles!reported_by(id, full_name, phone)
      `)
      .not('status', 'in', `(${INCIDENT_STATUS.RESOLVED},${INCIDENT_STATUS.CLOSED})`);

    if (filters.severity) {
      query = query.eq('severity', filters.severity);
    }

    if (filters.projectId) {
      query = query.eq('project_id', filters.projectId);
    }

    query = query
      .order('severity', { ascending: false })
      .order('created_at', { ascending: true });

    const { data, error } = await query;

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[INCIDENT] Get open incidents error:', error);
    return { data: null, error };
  }
};

const incidentService = {
  createIncident,
  getIncidents,
  getIncidentsByProject,
  getIncident,
  acknowledgeIncident,
  updateIncidentStatus,
  resolveIncident,
  closeIncident,
  uploadIncidentPhoto,
  getIncidentStats,
  getOpenIncidents,
};

export default incidentService;
